export type OrbitTenantKey = 'development' | 'production'

export interface OrbitTenantProfile {
  key: OrbitTenantKey
  label: string
  shortName: string
  tenantId: string
  clientId: string
  siteUrl: string
  tenantDomain: string
}

const REPO_DEFAULT_TENANTS: Record<OrbitTenantKey, Omit<OrbitTenantProfile, 'key'>> = {
  development: {
    label: 'RME Solutions (Dev)',
    shortName: 'RME',
    tenantId: 'fc21902f-c839-419c-9567-079c44c3e99c',
    clientId: '2fa09dae-6036-449d-9cd8-65e5abecb7e1',
    siteUrl: 'https://rmesolutions999.sharepoint.com/sites/ServiceDesk',
    tenantDomain: 'rmesolutions.com.au',
  },
  production: {
    label: 'ICG Construction',
    shortName: 'ICG',
    tenantId: 'dddf1fe9-4bda-417e-8075-5976b0cc5b96',
    clientId: '978c26a9-a52d-4e79-9ff8-21751624d79f',
    siteUrl: 'https://icgwagga.sharepoint.com/sites/IT-Operations',
    tenantDomain: 'icgwagga.com.au',
  },
}

export const ENV = import.meta.env.VITE_ENV || 'development'
export const IS_STAGING = import.meta.env.VITE_IS_STAGING === 'true'

export const readEnv = (key: string) => (((import.meta.env as Record<string, string | undefined>)[key]) || '').trim()
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

function splitCsv(value: string): string[] {
  return value
    .split(',')
    .map((entry) => entry.trim().toLowerCase())
    .filter(Boolean)
}

function readProfileValue(profile: OrbitTenantKey, envKeySuffix: string): string {
  const profilePrefix = profile === 'production' ? 'PRODUCTION' : 'DEVELOPMENT'
  const directValue = readEnv(`VITE_${profilePrefix}_${envKeySuffix}`)
  if (directValue) return directValue

  const genericValue = readEnv(`VITE_${envKeySuffix}`)
  if (!genericValue) return ''

  if (profile === 'production' && ENV === 'production') return genericValue
  if (profile === 'development' && ENV !== 'production') return genericValue
  return ''
}

function buildTenantProfile(profile: OrbitTenantKey): OrbitTenantProfile {
  const defaults = REPO_DEFAULT_TENANTS[profile]

  return {
    key: profile,
    label: readProfileValue(profile, 'TENANT_LABEL') || defaults.label,
    shortName: readProfileValue(profile, 'TENANT_SHORT_NAME') || defaults.shortName,
    tenantId: readProfileValue(profile, 'TENANT_ID') || defaults.tenantId,
    clientId: readProfileValue(profile, 'CLIENT_ID') || defaults.clientId,
    siteUrl: readProfileValue(profile, 'SHAREPOINT_SITE_URL') || defaults.siteUrl,
    tenantDomain: readProfileValue(profile, 'TENANT_DOMAIN') || defaults.tenantDomain,
  }
}

export const tenantProfiles: Record<OrbitTenantKey, OrbitTenantProfile> = {
  development: buildTenantProfile('development'),
  production: buildTenantProfile('production'),
}

const currentHost = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : ''

const liveHosts = splitCsv(readEnv('VITE_LIVE_HOSTS') || 'support.icgwagga.com.au')
const stagingHosts = splitCsv(readEnv('VITE_STAGING_HOSTS') || 'staging.icgwagga.com.au,staging.icg-service-desk.pages.dev')
const previewHostSuffixes = splitCsv(readEnv('VITE_PREVIEW_HOST_SUFFIXES') || '.icg-service-desk.pages.dev,.icg-service-desk-staging.pages.dev')

function matchesHost(hosts: string[], host: string): boolean {
  return hosts.includes(host)
}

function matchesHostSuffix(suffixes: string[], host: string): boolean {
  return suffixes.some((suffix) => host.endsWith(suffix))
}

export const currentHostName = currentHost
export const isProductionSupportHost = matchesHost(liveHosts, currentHost)
export const isPagesPreviewHost = matchesHostSuffix(previewHostSuffixes, currentHost)
export const isStagingCustomDomain = matchesHost(stagingHosts, currentHost) && !isPagesPreviewHost
export const isStagingHost = isStagingCustomDomain || IS_STAGING
export const isSandboxHost = isStagingHost || isPagesPreviewHost
export const shouldUseProductionTenant = ENV === 'production' || isProductionSupportHost || isStagingHost || isPagesPreviewHost
export const activeTenantProfile = shouldUseProductionTenant ? tenantProfiles.production : tenantProfiles.development
